import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { CategoryBadge, StatusBadge, LangBadge } from "@/components/CommentBits";
import { timeAgo } from "@/lib/time";
import { Radio, EyeOff, Trash2, Flag, Check, Undo2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const POLL_MS = 2000;
const LIMIT = 200;
const REMOVED = ["auto_hidden", "hidden", "deleted"];
const PASSED = ["kept", "approved"];

const TABS = [
  ["all", "All", () => true],
  ["passed", "Passed", (c) => PASSED.includes(c.status)],
  ["removed", "Hidden / deleted", (c) => REMOVED.includes(c.status)],
  ["review", "Needs review", (c) => c.status === "needs_review"],
];

const BTN = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold disabled:opacity-40";

// Every incoming comment as it lands, with what COMO did. Polls every 2 seconds; rows that arrive
// after the page opened flash briefly so a presenter can point at them. The same actions as the
// Review Queue, Passed Comments and Removal Log are available on each row.
export default function LiveComments() {
  const { user } = useAuth();
  const canAct = user?.role !== "client_viewer";
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const [params, setParams] = useSearchParams();
  const wanted = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "all";
  const focusId = params.get("id");
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: "delete" | "flag", comment }
  const known = useRef(null);
  const fresh = useRef(new Set());
  const hold = useRef(false);  // pointer over a row's buttons: don't shift rows under the cursor
  const [, bump] = useState(0);

  const accountsById = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const flagAction = (c) => (accountsById[c.account_id]?.rules?.action === "delete" ? "delete" : "hide");

  const load = useCallback(() => {
    if (!ready || document.hidden) return;
    const q = `?limit=${LIMIT}${scopeParam ? `&${scopeParam}` : ""}`;
    api.get(`/comments${q}`).then(({ data }) => {
      if (known.current === null) known.current = new Set();
      else data.forEach((c) => { if (!known.current.has(c.id)) fresh.current.add(c.id); });
      data.forEach((c) => known.current.add(c.id));
      setItems(data);
      setLoaded(true);
    }).catch(() => {});
  }, [ready, scopeParam]);

  useEffect(() => {
    if (!ready) return;
    api.get(`/accounts${scopeParam ? `?${scopeParam}` : ""}`).then(({ data }) => setAccounts(data)).catch(() => {});
  }, [ready, scopeParam]);

  useEffect(() => {
    if (!ready) return undefined;
    load();
    const t = setInterval(() => { if (!hold.current && !confirm) load(); }, POLL_MS);
    // Rows lose their flash and relative times refresh without waiting for a poll.
    const clear = setInterval(() => { fresh.current.clear(); bump((n) => n + 1); }, 6000);
    return () => { clearInterval(t); clearInterval(clear); };
  }, [ready, load, confirm]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMessage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      hold.current = false;
      load(); // a failed flag-and-remove still saves the flag, so always refresh
    }
  };

  const decide = (c, decision) => run(
    () => api.post(`/comments/${c.id}/decision`, { decision }),
    decision === "approve" ? "Kept" : decision === "delete" ? "Deleted" : "Hidden");
  const flag = (c) => run(() => api.post(`/comments/${c.id}/flag-missed`),
    flagAction(c) === "delete" ? "Flagged and deleted" : "Flagged and hidden");
  const undo = (c) => run(() => api.post(`/comments/${c.id}/undo`), "Comment restored");

  const confirmed = () => {
    const { kind, comment } = confirm;
    setConfirm(null);
    kind === "flag" ? flag(comment) : decide(comment, "delete");
  };
  const askFlag = (c) => (flagAction(c) === "delete" ? setConfirm({ kind: "flag", comment: c }) : flag(c));

  const counts = useMemo(() => Object.fromEntries(TABS.map(([k, , f]) => [k, items.filter(f).length])), [items]);
  // A comment opened from the ticker may have changed status since the card showed; fall back to All so it is still visible.
  const focused = focusId ? items.find((c) => c.id === focusId) : null;
  const tab = focused && !TABS.find(([k]) => k === wanted)[2](focused) ? "all" : wanted;
  const shown = useMemo(() => items.filter(TABS.find(([k]) => k === tab)[2]), [items, tab]);

  const scrolledTo = useRef(null);
  useEffect(() => {
    if (!focused || scrolledTo.current === focusId) return;
    scrolledTo.current = focusId;
    requestAnimationFrame(() => document.getElementById(`live-${focusId}`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
  }, [focused, focusId, shown]);

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6" data-testid="live-comments">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              {`Live, updating every 2 seconds · latest ${LIMIT} comments`}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Live Feed</h1>
          </div>
          <div className="flex items-center gap-3">
            <ClientPicker />
          </div>
        </div>

        <div className="flex flex-wrap gap-2" role="tablist">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              data-testid={`live-tab-${k}`}
              onClick={() => setParams(k === "all" ? {} : { tab: k })}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                tab === k ? "bg-foreground text-background border-foreground" : "bg-card text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {label} <span className={tab === k ? "opacity-60" : "text-slate-400"}>{counts[k]}</span>
            </button>
          ))}
        </div>

        {loaded && shown.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <Radio className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nothing here yet. New comments appear as they arrive.</p>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-slate-100 shadow-card overflow-hidden divide-y divide-slate-50">
            {shown.map((c) => (
              <div
                key={c.id}
                id={`live-${c.id}`}
                data-testid="live-row"
                data-focused={c.id === focusId ? "true" : undefined}
                className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 px-4 py-3 transition-colors duration-1000 ${
                  c.id === focusId ? "bg-amber-50 ring-2 ring-inset ring-amber-400" : fresh.current.has(c.id) ? "bg-indigo-50 animate-fade-in-up" : ""
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <StatusBadge status={c.status} />
                    <CategoryBadge category={c.ai?.category} />
                    <LangBadge language={c.language} />
                    {c.flagged && <span data-testid="flagged-badge" className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200">Flagged</span>}
                  </div>
                  <p className="text-sm text-slate-700 mb-1">{c.text}</p>
                  <p className="text-xs text-slate-400">
                    {c.author_name} · {c.account_name} · {timeAgo(c.ingested_at || c.created_at)}
                    {c.matched_rule && c.matched_rule.startsWith("always_remove:") ? ` · rule: ${c.matched_rule.split(":")[1]}` : ""}
                  </p>
                </div>
                {canAct && (
                  <div
                    className="flex flex-wrap gap-1.5 sm:justify-end shrink-0"
                    onMouseEnter={() => { hold.current = true; }}
                    onMouseLeave={() => { hold.current = false; }}
                  >
                    <RowActions c={c} busy={busy} flagAction={flagAction(c)}
                      onFlag={() => askFlag(c)} onDecide={(d) => (d === "delete" ? setConfirm({ kind: "delete", comment: c }) : decide(c, d))}
                      onUndo={() => undo(c)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting is permanent on {confirm?.comment.platform}. Restore only brings back hidden comments, not deleted ones.
              {confirm?.comment && <span className="block mt-2 text-slate-700">"{confirm.comment.text}"</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmed} data-testid="confirm-delete-yes">{confirm?.kind === "flag" ? "Flag & delete" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

// What a person can do depends on where the comment stands, as on the other moderation pages.
function RowActions({ c, busy, flagAction, onFlag, onDecide, onUndo }) {
  const hide = (
    <button key="hide" disabled={busy} onClick={() => onDecide("hide")} data-testid={`hide-${c.id}`} className={`${BTN} bg-red-50 text-red-600 hover:bg-red-100`}><EyeOff className="w-3.5 h-3.5" /> Hide</button>
  );
  const del = (
    <button key="delete" disabled={busy} onClick={() => onDecide("delete")} data-testid={`delete-${c.id}`} className={`${BTN} bg-slate-800 text-slate-50 hover:bg-slate-900`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
  );
  if (c.status === "needs_review") {
    return [
      <button key="keep" disabled={busy} onClick={() => onDecide("approve")} data-testid={`keep-${c.id}`} className={`${BTN} bg-green-50 text-green-700 hover:bg-green-100`}><Check className="w-3.5 h-3.5" /> Keep</button>,
      hide, del,
    ];
  }
  if (PASSED.includes(c.status)) {
    return [
      <button key="flag" disabled={busy} onClick={onFlag} data-testid={`flag-${c.id}`} className={`${BTN} bg-amber-50 text-amber-700 hover:bg-amber-100`}><Flag className="w-3.5 h-3.5" /> {c.flagged ? "Retry" : "Flag"} &amp; {flagAction}</button>,
      hide, del,
    ];
  }
  if (c.status === "deleted") return null; // permanent: nothing left to do
  if (REMOVED.includes(c.status)) {
    return (
      <button disabled={busy} onClick={onUndo} data-testid={`undo-${c.id}`} className={`${BTN} bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600`}><Undo2 className="w-3.5 h-3.5" /> Restore</button>
    );
  }
  return null;
}
